import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import type { ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import React, { createContext, Fragment, useEffect, useRef } from 'react';
import { fireEvent } from '../analytics/useForgeUiAnalyticsEvent';
import { useMetricsContext } from '../context';
import { type AppPerformanceProfilingEvent, getPerformanceMark } from '../performance-profiling';
import { getEnabledModulesForPerformanceProfiling } from './constants';

export type PerformanceProfileTimeline = {
	events: AppPerformanceProfilingEvent[];
	listeners: Set<(event: AppPerformanceProfilingEvent) => void>;
};

export const PerformanceAnalyticsContext: React.Context<
	React.RefObject<PerformanceProfileTimeline>
> = createContext<React.RefObject<PerformanceProfileTimeline>>(null!);

export const PERFORMANCE_ANALYTICS_INTERVAL = 10_000;

const offsetElapsedTimestamps = (
	events: AppPerformanceProfilingEvent[],
	elapsedBaselineMark: number,
) => {
	return events.map((event) => {
		const evaluatedElapsedTime = event.elapsed - elapsedBaselineMark;
		event.elapsed = evaluatedElapsedTime;
		return event;
	});
};

const isPerformanceAnalyticsEnabled = (extensionType: string) => {
	return (
		getEnabledModulesForPerformanceProfiling().includes(extensionType) &&
		fg('forge-ui-performance-analytics-provider')
	);
};

export const PerformanceAnalyticsProvider = ({
	children,
	appType,
	forgeEnvironment,
	profileKey,
	extensionType,
}: {
	appType: 'UIKit' | 'CustomUI';
	children: React.ReactNode;
	extensionType: string;
	forgeEnvironment: ForgeUIExtensionType['environmentType'];
	profileKey: string;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { page: source } = useMetricsContext();

	// Rely on refs to avoid re-rendering all children components when the state changes
	const performanceTimelineRef = useRef<PerformanceProfileTimeline>({
		events: [],
		listeners: new Set(),
	});
	const hasSent = useRef(false);
	const elapsedBaselineMark = useRef(0);

	useEffect(() => {
		hasSent.current = false;
		performanceTimelineRef.current.events = [];
		elapsedBaselineMark.current = getPerformanceMark();
		// Use `profileKey` to reset the all state when the extension or entryPoint changes to collect a new set of analytics
	}, [profileKey]);

	useEffect(() => {
		if (hasSent.current || !isPerformanceAnalyticsEnabled(extensionType || '')) {
			return;
		}

		// Send all collected performance related analytics in a single event
		const timeout = setTimeout(() => {
			// Clone the eventRef to avoid the possibility of mutating the original object
			const eventsPayload = structuredClone(performanceTimelineRef.current.events);

			if (fg('forge-ui-performance-sampling')) {
				fireEvent(createAnalyticsEvent, {
					action: 'performance.profiled',
					actionSubject: 'forge.ui.extension',
					attributes: {
						events: offsetElapsedTimestamps(eventsPayload, elapsedBaselineMark.current),
						// Time difference since this event was fired and since page loaded
						navigationTimeStart: elapsedBaselineMark.current,
						appType,
						forgeEnvironment,
					},
					source,
				});
			}

			hasSent.current = true;
		}, PERFORMANCE_ANALYTICS_INTERVAL);

		return () => {
			clearTimeout(timeout);
		};
	}, [
		appType,
		createAnalyticsEvent,
		elapsedBaselineMark,
		performanceTimelineRef,
		extensionType,
		forgeEnvironment,
		hasSent,
		source,
	]);

	return (
		<PerformanceAnalyticsContext.Provider value={performanceTimelineRef}>
			{children}
		</PerformanceAnalyticsContext.Provider>
	);
};

export const PerformanceAnalyticsProviderRollout: React.FC<
	{
		appType: 'UIKit' | 'CustomUI';
		children: React.ReactNode;
		extensionType: string;
		forgeEnvironment: ForgeUIExtensionType['environmentType'];
		profileKey: string;
	} & {
		children?: React.ReactNode | undefined;
	}
> = componentWithFG(
	'forge-ui-performance-analytics-provider',
	PerformanceAnalyticsProvider,
	Fragment,
);
